/**
 * Preconfigured Axios HTTP client for the Booge IT backend API.
 *
 * - Automatically attaches the Bearer JWT token from localStorage to every request.
 * - On a 401 response, clears stored credentials and redirects to the login page.
 *
 * @module api
 */

import axios from 'axios';

/** Base URL of the backend API — defaults to the local Azure Functions dev server. */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';

/** Shared Axios instance with auto-auth and error handling interceptors. */
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Decode a JWT payload and check whether the token has expired.
 * Returns true if expired or unparseable.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return !payload.exp || payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/** Attach the stored JWT as a Bearer token to every outgoing request.
 *  If the token is expired, clear auth state and redirect to login. */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    if (isTokenExpired(token)) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(new axios.Cancel('Token expired'));
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** On 401 responses, clear local auth state and redirect to the login page. */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Extract a human-readable error message from an Axios error.
 *
 * Handles both standard `{ detail: "string" }` responses and Pydantic
 * validation errors where `detail` is an array of objects with `msg` fields.
 */
export function getErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const msg = detail[0]?.msg;
    if (typeof msg === 'string') return msg;
  }
  return fallback;
}

export default api;
